import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PLAYERS } from "../lib/quattro";
import { getInitials, colorFromName } from "../lib/avatar";

const QUICK_EMOJI = ["👋", "🔥", "🎉", "😅", "🤔", "💀", "🤝", "🍀"];

/**
 * In-room chat panel. Receives a `messages` list (from parent's WS subscription)
 * and a `onSend(text)` callback. Stays small and dense for mobile.
 */
export default function ChatPanel({ messages = [], onSend, mySeat, players }) {
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef(null);
  const lastSeenLenRef = useRef(messages.length);

  useEffect(() => {
    if (open) {
      lastSeenLenRef.current = messages.length;
      setUnread(0);
      // Scroll to bottom on new message
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      });
    } else {
      const newCount = Math.max(0, messages.length - lastSeenLenRef.current);
      setUnread(newCount);
    }
  }, [messages, open]);

  const submit = (text) => {
    const t = String(text || draft).trim();
    if (!t) return;
    onSend?.(t.slice(0, 200));
    setDraft("");
  };

  return (
    <>
      {/* Floating toggle button (visible when collapsed) */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-toggle-btn"
          className="fixed z-[80] glass rounded-full px-4 py-3 flex items-center gap-2 hover:bg-white/10"
          style={{
            right: 16,
            // Sit ABOVE the preview-env "Made with Emergent" badge (bottom-right) so QA can reach it
            bottom: 64,
            border: "1px solid rgba(255,255,255,0.14)",
            boxShadow: "0 6px 22px rgba(0,0,0,0.5)",
          }}
        >
          <span style={{ fontSize: 18 }}>💬</span>
          <span className="font-display font-bold text-xs">Chat</span>
          {unread > 0 && (
            <span
              className="rounded-full font-mono font-bold flex items-center justify-center"
              style={{
                background: "#FF2A2A",
                color: "#fff",
                fontSize: 10,
                minWidth: 18,
                height: 18,
                padding: "0 5px",
              }}
              data-testid="chat-unread-count"
            >{unread}</span>
          )}
        </button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            key="chatpanel"
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="fixed z-[80] glass rounded-2xl flex flex-col"
            style={{
              right: 16,
              bottom: 64,
              width: "min(360px, calc(100vw - 32px))",
              height: 420,
              border: "1px solid rgba(255,255,255,0.14)",
              boxShadow: "0 10px 40px rgba(0,0,0,0.55)",
            }}
            data-testid="chat-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <span className="label-eyebrow" style={{ color: "#FFD700" }}>ROOM CHAT</span>
              <button
                onClick={() => setOpen(false)}
                className="text-white/60 hover:text-white text-xs"
                data-testid="chat-close-btn"
              >Close</button>
            </div>

            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-3 py-3 space-y-2 scroll-y"
              data-testid="chat-messages"
            >
              {messages.length === 0 ? (
                <div className="text-center text-white/40 text-xs italic mt-4">
                  No messages yet. Say hi 👋
                </div>
              ) : messages.map((m, i) => {
                const isMe = m.seat === mySeat;
                const seatColor = PLAYERS[m.seat]?.color || "#fff";
                const ringColor = colorFromName(m.name || "?");
                return (
                  <div
                    key={i}
                    className={`flex items-end gap-2 ${isMe ? "flex-row-reverse" : ""}`}
                    data-testid={`chat-msg-${i}`}
                  >
                    <div
                      className="rounded-full flex items-center justify-center font-display font-black flex-shrink-0"
                      style={{
                        width: 26, height: 26,
                        background: `linear-gradient(155deg, ${seatColor}, #111)`,
                        color: m.seat === 0 ? "#0B0C10" : "#fff",
                        fontSize: 10, letterSpacing: 0.5,
                        border: `2px solid ${ringColor}`,
                      }}
                    >{getInitials(m.name || "?")}</div>
                    <div
                      className="rounded-2xl px-3 py-2 max-w-[80%]"
                      style={{
                        background: isMe ? "rgba(255,215,0,0.10)" : "rgba(255,255,255,0.06)",
                        border: `1px solid ${isMe ? "rgba(255,215,0,0.25)" : "rgba(255,255,255,0.10)"}`,
                      }}
                    >
                      <div className="text-[10px] opacity-60 mb-0.5" style={{ color: seatColor }}>
                        {m.name}{isMe ? " (you)" : ""}
                      </div>
                      <div className="text-sm break-words">{m.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-1 px-3 py-2 border-t border-white/10 overflow-x-auto">
              {QUICK_EMOJI.map((e) => (
                <button
                  key={e}
                  onClick={() => submit(e)}
                  className="rounded-full px-2 py-1 hover:bg-white/10"
                  style={{ fontSize: 16 }}
                  data-testid={`chat-emoji-${e}`}
                  title={`Send ${e}`}
                >{e}</button>
              ))}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); submit(); }}
              className="flex gap-2 px-3 py-2 border-t border-white/10"
            >
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Say something…"
                maxLength={200}
                className="flex-1 bg-white/5 border border-white/15 rounded-full px-3 py-2 text-sm text-white outline-none focus:border-yellow-400/60"
                data-testid="chat-input"
              />
              <button
                type="submit"
                className="rounded-full px-4 py-2 font-display font-bold text-xs"
                style={{ background: "#FFD700", color: "#0B0C10" }}
                data-testid="chat-send-btn"
                disabled={!draft.trim()}
              >Send</button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
